import { useEffect, useState, type FormEvent } from 'react';
import { trocarMinhaSenha } from '../autenticacao/cognito.js';
import { CampoDeSenha } from './CampoDeSenha.js';
import { CamposDeNovaSenha, problemaDaSenha } from './CamposDeNovaSenha.js';

/**
 * Troca da própria senha, com a sessão aberta.
 *
 * Pede a senha atual de propósito: sem isso, uma estação destravada e sem dono viraria
 * uma conta sequestrada — quem sentasse na cadeira alheia trocaria a senha sem saber a
 * que está em uso, e o dono perderia o acesso a um cockpit que gera cobrança.
 *
 * Não usa o `Dialogo` comum porque aquele tem um campo de texto só; aqui são três, e
 * dois deles precisam ser do tipo `password`.
 */
export function TrocarSenha({ aoFechar }: { aoFechar: () => void }) {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState('');
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();

    const problema = problemaDaSenha(nova, confirmacao);
    if (problema) {
      setErro(problema);
      return;
    }
    if (nova === senhaAtual) {
      setErro('A nova senha precisa ser diferente da atual.');
      return;
    }

    setErro('');
    setEnviando(true);
    try {
      await trocarMinhaSenha(senhaAtual, nova);
      setPronto(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível trocar a senha.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fundo-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Trocar senha"
      onClick={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div className="modal">
        <h3>Trocar minha senha</h3>

        {pronto ? (
          <>
            <div className="aviso ok" style={{ marginTop: 14 }}>
              Senha trocada. Ela já vale no próximo acesso — a sessão aberta continua válida.
            </div>
            <div className="modal-acoes">
              <button className="bt pequeno" onClick={aoFechar}>
                Fechar
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={enviar}>
            <div style={{ marginTop: 14 }}>
              <CampoDeSenha
                id="senha-atual"
                rotulo="Senha atual"
                valor={senhaAtual}
                aoMudar={setSenhaAtual}
                autoComplete="current-password"
                autoFocus
              />
            </div>

            <CamposDeNovaSenha
              prefixo="trocar"
              nova={nova}
              confirmacao={confirmacao}
              aoMudarNova={setNova}
              aoMudarConfirmacao={setConfirmacao}
            />

            {erro && (
              <div className="aviso erro" style={{ marginTop: 12 }}>
                {erro}
              </div>
            )}

            <div className="modal-acoes">
              <button
                type="button"
                className="bt vazado pequeno"
                onClick={aoFechar}
                disabled={enviando}
              >
                Cancelar
              </button>
              <button className="bt pequeno" type="submit" disabled={enviando}>
                {enviando ? 'Trocando…' : 'Trocar senha'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
